import { ISilhouetteData, ISilhouetteParameters } from '../silhouette-interfaces';
import { DistanceType } from '../../cluster-interfaces';
import {
    euclideanDistance, euclideanDistanceExplanation,
    getPointString,
    manhattanDistance,
    manhattanDistanceExplanation,
    round
} from '../../cluster-provider';

const calculateA = (
    distance: DistanceType,
    pointIndex: number,
    point: number[],
    currentCluster: number[][]
): { value: number, explanation: string } => {

    const clusterLength = currentCluster.length;
    let sum = 0;

    const parts: string[] = [];

    for (let j = 0; j < clusterLength; j++) {
        if (pointIndex === j) continue;

        const point2 = currentCluster[j];
        let d = 0;

        switch (distance) {
            case DistanceType.Euclidean:
                d = euclideanDistance(point, point2);
                parts.push(euclideanDistanceExplanation(point, point2, false));
                break;
            case DistanceType.Manhattan:
                d = manhattanDistance(point, point2);
                parts.push(manhattanDistanceExplanation(point, point2, false));
                break;
        }

        d = round(d);
        sum += d;
    }

    const average = round(sum / (clusterLength - 1));
    const explanation = `A: (${ parts.join(' + ') }) / ${ clusterLength - 1 } = ${ average }`;
    return { value: average, explanation };
};

const calculateB = (
    distance: DistanceType,
    point: number[],
    otherCluster: number[][]
): { value: number, explanation: string } => {

    const clusterLength = otherCluster.length;
    let sum = 0;
    const parts: string[] = [];

    for (let j = 0; j < clusterLength; j++) {
        const point2 = otherCluster[j];
        let d = 0;

        switch (distance) {
            case DistanceType.Euclidean:
                d = euclideanDistance(point, point2);
                break;
            case DistanceType.Manhattan:
                d = manhattanDistance(point, point2);
                break;
        }

        d = round(d);
        sum += d;
        parts.push(`${d}`);
    }

    const average = round(sum / clusterLength);
    const explanation = `B: (${parts.join(' + ')}) / ${clusterLength} = ${average}`;
    return { value: average, explanation };
};

export const getSilhouetteData = (params: ISilhouetteParameters, distance: DistanceType): ISilhouetteData => {

    const data: ISilhouetteData = {
        results: [],
        calculations: [],
    };

    const process = (cluster: number[][], otherCluster: number[][]) => {
        for(let i=0; i<cluster.length; i++) {
            const point1 = cluster[i];

            let silhouette = 0;
            let aStr = '';
            let bStr = '';
            let silhouetteStr = '';

            if(cluster.length > 1) {
                const { value: a, explanation: aExplanation } = calculateA(distance, i, point1, cluster);
                const { value: b, explanation: bExplanation } = calculateB(distance, point1, otherCluster);
                silhouette = (b - a) / (Math.max(a, b));

                aStr = aExplanation;
                bStr = bExplanation;
                silhouetteStr = `(${ b } - ${ a }) / max(${ b }, ${ a })`;
            }

            silhouette = round(silhouette);


            data.results.push({
                point: point1,
                silhouette,
            });

            data.calculations.push(`Point ${ getPointString(point1) } → ${aStr}, ${bStr}, silhouette = ${ silhouetteStr } = ${silhouette}`);
        }
    };

    process(params.cluster1, params.cluster2);
    process(params.cluster2, params.cluster1);

    return data;
};